<template>
  <EXTooltip
    v-if="ideaMDP452 && tooltipTitle"
    placement="top"
    :title="tooltipTitle"
    :delay="200"
  >
    <span
      class="inspection-inspected-status-badge"
      :class="[`inspection-inspected-status-badge--${state}`]"
    >
      <span v-if="glyph" class="inspection-inspected-status-badge__glyph" aria-hidden="true">
        {{ glyph }}
      </span>
      <span class="inspection-inspected-status-badge__label">
        {{ label }}
      </span>
    </span>
  </EXTooltip>
  <span
    v-else-if="ideaMDP452"
    class="inspection-inspected-status-badge"
    :class="[`inspection-inspected-status-badge--${state}`]"
  >
    <span v-if="glyph" class="inspection-inspected-status-badge__glyph" aria-hidden="true">
      {{ glyph }}
    </span>
    <span class="inspection-inspected-status-badge__label">
      {{ label }}
    </span>
  </span>
</template>

<script setup>
import { computed } from "vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { formatInspectionDate } from "@components/utils/buildCheckpointDrawerSections";

/** Canonical inspectionStatus values, normalised to stable state keys. */
const STATE_BY_VALUE = {
  "inspection-pending": "pending",
  passed: "passed",
  failed: "failed",
  "not-required": "not-required",
};

const LABEL_BY_STATE = {
  pending: "Inspection pending",
  passed: "Inspection passed",
  failed: "Inspection failed",
  "not-required": "Inspection not required",
};

/** Leading ✓ / ✕ are text glyphs (Inter Semi Bold 11px), not icon components. */
const GLYPH_BY_STATE = {
  passed: "✓",
  failed: "✕",
};

/** inspected_by is a name string on the v2 checklist rows; accept the AccessLimitedSerializer object shape too. */
function resolveInspectorName(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  const name = value?.name || value?.fullName || value?.full_name;
  return typeof name === "string" ? name.trim() : "";
}

const props = defineProps({
  /**
   * Feature flag `idea-MDP-452` (New Checkpoint Edit and Inspection, MDP-452). This badge is
   * new behaviour and only renders when the flag is enabled — the parent resolves the
   * LaunchDarkly value (account/src/store/modules/featureFlags.js) and passes it in.
   */
  ideaMDP452: {
    type: Boolean,
    default: false,
  },
  /**
   * State of the checkpoint's inspection step. Maps 1:1 to the Figma Inspection Status
   * variant axis (node 46998:193171); "Inspection pending" is the design default.
   */
  inspectionStatus: {
    type: String,
    required: true,
    // Self-contained so it survives defineProps hoisting in Vue 2.7's compiler-sfc.
    validator(value) {
      const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "-");
      return ["inspection-pending", "passed", "failed", "not-required"].includes(normalized);
    },
  },
  /** Timestamp the inspection was completed (ER-11856). */
  inspectedOn: {
    type: String,
    default: null,
  },
  /** Inspector name (or AccessLimitedSerializer {name, picture} object) (ER-11856). */
  inspectedBy: {
    type: [String, Object],
    default: null,
  },
});

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "-");
}

const state = computed(() => STATE_BY_VALUE[normalize(props.inspectionStatus)] || "pending");

const label = computed(() => LABEL_BY_STATE[state.value]);

const glyph = computed(() => GLYPH_BY_STATE[state.value] || "");

const tooltipTitle = computed(() => {
  if (state.value !== "passed" && state.value !== "failed") return "";
  const name = resolveInspectorName(props.inspectedBy);
  const date = formatInspectionDate(props.inspectedOn);
  if (!name || !date) return "";
  return `By ${name} on ${date}`;
});
</script>

<style scoped>
.inspection-inspected-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: fit-content;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
  border: 1px solid;
  border-radius: 9999px; /* radius-full */
  cursor: default;
  user-select: none;
}

/* pending: shared 2.0 Basic Badges base — spacing-md 8px horizontal, spacing-xxs 2px
   vertical, Inter Medium 12px / 18px line height, 1px border. */
.inspection-inspected-status-badge--pending {
  padding: var(--spacing-xxs, 2px) var(--spacing-md, 8px);
  color: var(--component-colors-utility-gray-utility-gray-700, #344054);
  background: var(--component-colors-utility-gray-utility-gray-50, #f9fafb);
  border-color: var(--component-colors-utility-gray-utility-gray-200, #eaecf0);
}

.inspection-inspected-status-badge--pending .inspection-inspected-status-badge__label {
  line-height: 18px;
}

/* not-required: shared 2.0 Basic Badges base, no fill and a heavier 1.5px #475467 outline. */
.inspection-inspected-status-badge--not-required {
  padding: var(--spacing-xxs, 2px) var(--spacing-md, 8px);
  color: var(--component-colors-utility-gray-utility-gray-700, #344054);
  background: transparent;
  border: 1.5px solid #475467;
}

.inspection-inspected-status-badge--not-required .inspection-inspected-status-badge__label {
  line-height: 18px;
}

/* passed / failed: hand-built — 9px horizontal / 3px vertical padding, 5px gap,
   line-height normal; the leading ✓ / ✕ is a text glyph (Inter Semi Bold 11px) beside an
   Inter Medium 12px label. */
.inspection-inspected-status-badge--passed,
.inspection-inspected-status-badge--failed {
  gap: 5px;
  padding: 3px 9px;
}

.inspection-inspected-status-badge--passed {
  color: #067647;
  background: #ecfdf3;
  border-color: #aaefc6;
}

.inspection-inspected-status-badge--failed {
  color: #b42318;
  background: #fef3f2;
  border-color: #fecdca;
}

.inspection-inspected-status-badge__glyph {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600; /* Inter Semi Bold */
  line-height: normal;
}
</style>
